import {
  Component,
  Inject,
  signal,
  computed,
  InputSignal,
  input,
} from '@angular/core';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { LogService } from '@/app/core/services/log.service';
import {
  ManagementListComponent,
  ManagementListItem,
} from '@/app/modules/management-list/management-list.component';

export interface ManagementDialogData {
  pageName: string;
  gridName: string;
}

@Component({
  selector: 'rdo-saved-views-manager-dialog',
  styleUrls: ['./saved-views-dialogs.component.scss'],
  styles: `
    .saved-views-manager-dialog {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .content {
      display: block;
      width: 100%;
    }
    .actions {
      display: flex;
      justify-content: space-between;
      gap: 4px;
      padding: 1.5rem;
      align-items: flex-end;
      flex-wrap: nowrap;
      flex: 0;
    }

    .saving-indicator {
      font-size: 1.2rem;
    }

    .actions-left {
      flex: 1;
    }

    .actions-right {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
  `,
  template: `
    <div [id]="id()" class="saved-views-manager-dialog saved-views-dialog flex">
      <h2 mat-dialog-title class="header">
        {{ title() }}
      </h2>
      <div mat-dialog-content class="content">
        <rdo-management-list
          [config]="config()"
          [form]="form"
          [columnHeaders]="columnHeaders()"
        />
      </div>
      <div mat-dialog-actions class="actions">
        <div class="actions-left"></div>
        <div class="actions-right">
          @if (isSaving()) {
            <div class="saving-indicator">
              <div class="rdo-loader">
                <img
                  src="/assets/images/loader-sm.gif"
                  width="20"
                  height="20"
                />
              </div>
            </div>
          }
          <button
            mat-button
            (click)="onOk()"
            class="btn"
            [disabled]="!form.valid || isSaving()"
          >
            Ok
          </button>
          <button mat-button mat-dialog-close class="btn">
            {{ 'main.core.common.cancel' | translate }}
          </button>
        </div>
      </div>
    </div>
  `,
  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslatePipe,
    ManagementListComponent,
  ],
  providers: [LogService],
})
export class SavedViewsManagerDialogComponent {
  public gridName: InputSignal<string> = input('');
  public pageName: InputSignal<string> = input('');
  public config = signal(null);
  public columnHeaders = signal(['', '', '']);
  public id = computed(() => {
    return `Manager-${this.gridName()}`;
  });
  public title = computed(() => {
    const reports = this.translateService.instant('main.core.common.reports');
    return `${this.data.pageName} ${reports}`;
  });
  public isSaving = signal(false);

  public form = new FormGroup({});

  constructor(
    private dialogRef: MatDialogRef<SavedViewsManagerDialogComponent>,
    private translateService: TranslateService,
    private savedViewsService: SavedViewsService,
    private logger: LogService,
    @Inject(MAT_DIALOG_DATA) public data: ManagementDialogData
  ) {}

  ngOnInit(): void {
    this.config.set(this.buildConfig());
    const defaultText = this.translateService.instant(
      'main.filters.profiles.manage.default'
    );
    this.columnHeaders.set(['', defaultText, '']);
  }

  // batch update meta changes
  private async processMetaChanges(): Promise<void> {
    const formValue = this.form.value;
    // Get all views from the service's Map
    const allViews = Array.from(this.savedViewsService.views().values());
    const updatedViews: any[] = [];
    // const deleteOps: Array<Promise<any>> = [];

    // Collect deletions and accumulate meta changes
    for (const [key, formGroupValue] of Object.entries(formValue)) {
      const formGroup = this.form.get(key) as FormGroup;
      if (!formGroup || !formGroup.dirty) {
        continue;
      }

      const typedValue = formGroupValue as {
        name: string;
        isDefault: boolean;
        isDeleted: boolean;
      };

      // Find the view in allViews
      const view = allViews.find((v) => v.saved_view_id === key);
      if (!view) {
        continue;
      }

      // Handle deletion - we will filter these out before the PUT, but we need to know which ones are deleted to exclude them
      // Processing deletions synchronously here to ensure they complete.  There we issues trying to process deletions in a batch.
      if (typedValue.isDeleted) {
        await this.savedViewsService.delete(view, false);
        continue;
      }

      // Update only name and parent_saved_view_id
      view.name =
        typedValue.name !== undefined
          ? this.savedViewsService.getSerializedViewName(typedValue.name)
          : view.name;
      view.parent_saved_view_id = typedValue.isDefault ? 'default' : '';
    }

    // After processing, add all views to the payload
    updatedViews.push(...allViews);

    // Filter out deleted views before PUT
    const filteredViews = updatedViews.filter(
      (v) => !formValue[v.saved_view_id]?.isDeleted
    );

    // Batch update meta changes
    if (filteredViews.length > 0) {
      // We had an issue where a SavedView somehow had no "saved_view_id", which broke the delete process.
      // This enforce algo adds an id if it's missing and the DB replaces it with a correct id.
      await this.savedViewsService.put({
        saved_views: this.savedViewsService.enforceSavedViewIds(filteredViews),
      });
    }
  }

  private hasChanges(): boolean {
    return this.form.dirty;
  }

  async onOk(): Promise<void> {
    try {
      if (this.hasChanges()) {
        this.isSaving.set(true);
        await this.processMetaChanges();
        await this.savedViewsService.load();
        this.isSaving.set(false);
      }

      this.dialogRef.close({ success: true });
    } catch (error) {
      console.error('Error processing changes:', error);
    }
  }

  private buildConfig(): ManagementListItem[] {
    const views = this.savedViewsService.currentGridViewsList();
    return views.map((view) => {
      return {
        name: this.savedViewsService.deserializeViewName(view.name),
        isDefault: view.parent_saved_view_id === 'default',
        key: view.saved_view_id,
        canDelete: true,
      };
    });
  }

  private processOldDefaultViews(
    newDefaultViewId: string,
    operations: Array<{ id: string; operation: () => Promise<any> }>
  ): void {
    // Find all views that are currently set as default (excluding the new default)
    const oldDefaultViews = this.savedViewsService
      .allViewsList()
      .filter(
        (view) =>
          view.parent_saved_view_id === 'default' &&
          view.saved_view_id !== newDefaultViewId
      );

    // Update each old default view to no longer be default
    oldDefaultViews.forEach((view) => {
      console.log(
        `     Removing default status from old default view: ${view.name} (${view.saved_view_id})`
      );

      const updatedView = {
        ...view,
        parent_saved_view_id: '',
      };

      operations.push({
        id: view.saved_view_id,
        operation: () => this.savedViewsService.patch(updatedView, true, false),
      });
    });

    if (oldDefaultViews.length > 0) {
      console.log(
        `     Found and queued ${oldDefaultViews.length} old default view(s) for update`
      );
    }
  }

  private async processChanges(): Promise<void> {
    const formValue = this.form.value;
    const operations: Array<{ id: string; operation: () => Promise<any> }> = [];
    console.log('~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~');
    console.log('~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~');
    console.log('saved-views-manager-dialog.component.processChanges  ');

    // Loop through each form control and collect operations
    for (const [key, formGroupValue] of Object.entries(formValue)) {
      const formGroup = this.form.get(key) as FormGroup;

      // Only process dirty form groups
      if (!formGroup || !formGroup.dirty) {
        continue;
      }

      const typedValue = formGroupValue as {
        name: string;
        isDefault: boolean;
        isDeleted: boolean;
      };

      // Find the corresponding view in the service
      const view = this.savedViewsService
        .allViewsList()
        .find((v) => v.saved_view_id === key);

      if (!view) {
        console.warn(`View with id ${key} not found`);
        continue;
      }

      // Handle deletion
      if (typedValue.isDeleted) {
        console.log(`Deleting view: ${view.name} (${key})`);
        operations.push({
          id: key,
          operation: () => this.savedViewsService.delete(view, false),
        });
        continue;
      }

      // Prepare updated view
      const updatedView = { ...view };
      let needsUpdate = false;

      // Update name if changed
      if (typedValue.name && typedValue.name !== view.name) {
        updatedView.name = this.savedViewsService.getSerializedViewName(
          typedValue.name
        );
        needsUpdate = true;
      }

      // Update default status if changed
      if (typedValue.isDefault !== (view.parent_saved_view_id === 'default')) {
        console.log(
          '     updating default status for view:',
          view.name,
          'isDefault:',
          typedValue.isDefault
        );
        updatedView.parent_saved_view_id = typedValue.isDefault
          ? 'default'
          : '';
        needsUpdate = true;

        // If setting this view as the new default, remove default status from old default view(s)
        // There should only be one default view, but we will handle multiple just in case.
        if (typedValue.isDefault) {
          this.processOldDefaultViews(key, operations);
        }
      }

      // Only patch if there were changes
      if (needsUpdate) {
        console.log(`     updating view: ${view.name} (${key})`, updatedView);
        operations.push({
          id: key,
          operation: () =>
            this.savedViewsService.patch(updatedView, true, false),
        });
      }
    }

    // Await a promise with a timeout of 2000ms
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Process operations in batches of 3
    if (operations.length > 0) {
      const errors = new Map<string, Error>();
      const batchSize = 3;

      for (let i = 0; i < operations.length; i += batchSize) {
        const batch = operations.slice(i, i + batchSize);
        console.log(
          `       Processing batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(operations.length / batchSize)}`
        );

        // Process each operation in the batch
        // Promise.allSettled waits for all promises to complete (success or failure)
        // and returns an array with status: 'fulfilled' or 'rejected' for each
        const batchResults = await Promise.allSettled(
          batch.map((op) => op.operation())
        );

        // Check each result and collect errors
        batchResults.forEach((result, index) => {
          const op = batch[index];

          if (result.status === 'fulfilled') {
            console.log(
              `       Successfully processed operation for view: ${op.id}`
            );
          } else {
            // result.status === 'rejected'
            console.error(
              `       Error processing view ${op.id}:`,
              result.reason
            );
            errors.set(op.id, result.reason as Error);
          }
        });

        // Log batch completion
        const successCount = batchResults.filter(
          (r) => r.status === 'fulfilled'
        ).length;
        const failCount = batchResults.filter(
          (r) => r.status === 'rejected'
        ).length;
        console.log(
          `       Batch completed: ${successCount} succeeded, ${failCount} failed`
        );
      }

      // Report final results
      if (errors.size > 0) {
        console.error(
          `       Processing completed with ${errors.size} error(s):`,
          Array.from(errors.entries())
        );
        // Store errors for potential display or logging
        (this as any).processingErrors = errors;
      } else {
        console.log('       All changes processed successfully');
      }

      console.log('~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~');
      console.log('~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~-~');
    }
  }
}
